import React, { useState } from "react";

// Country codes data
const countryCodes = [
  { code: "+234", country: "Nigeria", flag: "🇳🇬" },
  { code: "+1", country: "United States", flag: "🇺🇸" },
  { code: "+44", country: "United Kingdom", flag: "🇬🇧" },
  { code: "+91", country: "India", flag: "🇮🇳" },
  { code: "+86", country: "China", flag: "🇨🇳" },
  { code: "+81", country: "Japan", flag: "🇯🇵" },
  { code: "+49", country: "Germany", flag: "🇩🇪" },
  { code: "+33", country: "France", flag: "🇫🇷" },
  { code: "+39", country: "Italy", flag: "🇮🇹" },
  { code: "+34", country: "Spain", flag: "🇪🇸" },
  { code: "+7", country: "Russia", flag: "🇷🇺" },
  { code: "+55", country: "Brazil", flag: "🇧🇷" },
  { code: "+52", country: "Mexico", flag: "🇲🇽" },
  { code: "+61", country: "Australia", flag: "🇦🇺" },
  { code: "+27", country: "South Africa", flag: "🇿🇦" },
  { code: "+254", country: "Kenya", flag: "🇰🇪" },
  { code: "+233", country: "Ghana", flag: "🇬🇭" },
  { code: "+20", country: "Egypt", flag: "🇪🇬" },
  { code: "+971", country: "UAE", flag: "🇦🇪" },
  { code: "+966", country: "Saudi Arabia", flag: "🇸🇦" },
  { code: "+65", country: "Singapore", flag: "🇸🇬" },
  { code: "+60", country: "Malaysia", flag: "🇲🇾" },
  { code: "+62", country: "Indonesia", flag: "🇮🇩" },
  { code: "+63", country: "Philippines", flag: "🇵🇭" },
  { code: "+82", country: "South Korea", flag: "🇰🇷" },
  { code: "+66", country: "Thailand", flag: "🇹🇭" },
  { code: "+84", country: "Vietnam", flag: "🇻🇳" },
  { code: "+92", country: "Pakistan", flag: "🇵🇰" },
  { code: "+880", country: "Bangladesh", flag: "🇧🇩" },
  { code: "+94", country: "Sri Lanka", flag: "🇱🇰" },
];

type PhoneInputProps = {
  label?: string;
  placeholder?: string;
  name: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  status?: "warning" | "";
  required?: boolean;
  container?: string;
  readOnly?: boolean;
};

const PhoneInput: React.FC<PhoneInputProps> = (props) => {
  const [selectedCountryCode, setSelectedCountryCode] = useState("+234");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const borderColor = props.status ? "border-[red]" : "border-[#0B60D6]";

  // Filter country codes based on search
  const filteredCountryCodes = countryCodes.filter(
    (country) =>
      country.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.code.includes(searchTerm)
  );

  // Handle country code selection
  const handleCountryCodeSelect = (code: string) => {
    setSelectedCountryCode(code);
    setIsDropdownOpen(false);
    setSearchTerm("");

    // Trigger onChange with updated phone number
    if (props.onChange && props.value) {
      const phoneNumber = props.value.replace(/^\+\d+\s*/, ""); // Remove existing country code
      const event = {
        target: {
          name: props.name,
          value: `${code} ${phoneNumber}`,
        },
      } as React.ChangeEvent<HTMLInputElement>;
      props.onChange(event);
    }
  };

  // Handle phone number input
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let phoneNumber = e.target.value;
   
    phoneNumber = phoneNumber.replace(/\D/g, "");
    phoneNumber = phoneNumber.slice(0, 11);

    // Create a new event with country code + phone number
    const newEvent = {
      target: {
        name: props.name,
        value: `${selectedCountryCode} ${phoneNumber}`,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    if (props.onChange) {
      props.onChange(newEvent);
    }
  };

  // Extract phone number without country code for display
  const getPhoneNumberOnly = () => {
    if (!props.value) return "";
    return props.value.replace(/^\+\d+\s*/, "");
  };

  // Get selected country flag
  const getSelectedCountryFlag = () => {
    const country = countryCodes.find((c) => c.code === selectedCountryCode);
    return country ? country.flag : "🌍";
  };

  return (
    <div className={`${props.container} font-abel mb-2 w-full`}>
      {props.label && (
        <label className="block text-[#1E1E1E] text-[15px] capitalize mb-1">
          {props.label}{" "}
          {props.required && <span className="text-[red]">*</span>}
        </label>
      )}
      <div
        className={`flex text-[15px] rounded-[10px] border hover:border-[#243677] bg-[#ffffff] focus-within:border-[#243677] h-[45.5px] ${borderColor}`}
      >
        {/* Country Code Dropdown */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-1 px-3 h-full border-r border-[#e8e8e8] hover:bg-[#f5f5f5] rounded-l-[10px] min-w-[90px]"
          >
            <span className="text-[18px]">{getSelectedCountryFlag()}</span>
            <span className="text-[14px] font-medium">
              {selectedCountryCode}
            </span>
            <svg
              className={`w-4 h-4 transition-transform ${
                isDropdownOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute top-full left-0 mt-1 w-[280px] bg-white border border-[#e8e8e8] rounded-lg shadow-lg z-50 max-h-[300px] overflow-hidden">
              {/* Search Input */}
              <div className="p-2 border-b border-[#e8e8e8]">
                <input
                  type="text"
                  placeholder="Search country..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 text-[14px] border border-[#e8e8e8] rounded-md outline-none focus:border-[#243677]"
                />
              </div>

              {/* Country List */}
              <div className="overflow-y-auto max-h-[240px]">
                {filteredCountryCodes.map((country) => (
                  <button
                    key={country.code + country.country}
                    type="button"
                    onClick={() => handleCountryCodeSelect(country.code)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#f5f5f5] ${
                      selectedCountryCode === country.code ? "bg-[#e3f2fd]" : ""
                    }`}
                  >
                    <span className="text-[18px]">{country.flag}</span>
                    <span className="text-[14px] flex-1">
                      {country.country}
                    </span>
                    <span className="text-[14px] text-gray-600">
                      {country.code}
                    </span>
                  </button>
                ))}
                {filteredCountryCodes.length === 0 && (
                  <div className="px-3 py-4 text-center text-gray-500 text-[14px]">
                    No countries found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Phone Number Input */}
        <div className="flex-1 flex items-center px-4">
          <input
            name={props.name}
            value={getPhoneNumberOnly()}
            placeholder={props.placeholder || "Enter phone number"}
            onChange={handlePhoneChange}
            type="tel"
            readOnly={props.readOnly}
            className="border-none placeholder:font-abel text-[15px] placeholder:text-[15px] placeholder:text-[#98A2B3] outline-none w-full h-full"
          />
        </div>
      </div>

      {/* Click outside to close dropdown */}
      {isDropdownOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsDropdownOpen(false)}
        />
      )}
    </div>
  );
};

export default PhoneInput;
